/**
 * TEXTPRINT — Sous-traitants (Sprint 2 MVP).
 * Brief SIM-PLATFORM-TEXTIPRINT-2026-001 §H.
 *
 * MVP : KPIs + filtres + table + toggle statut. Création/édition complète
 * arrive en Sprint 2.5.
 */
import { useMemo, useState } from 'react';
import { RefreshCw, AlertTriangle, Handshake, Star, Clock } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { useSousTraitants, useUpdateSousTraitant } from '@/hooks/useTextprintSprint2';
import {
  STATUT_SOUS_TRAITANT_LABELS, SPECIALITE_SOUS_TRAITANT_LABELS,
  type SousTraitant, type StatutSousTraitant, type SpecialiteSousTraitant,
} from '@/types';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';

const STATUT_BADGE: Record<StatutSousTraitant, string> = {
  actif:      'ds-bd ds-bd-s',
  inactif:    'ds-bd ds-bd-g',
  blackliste: 'ds-bd ds-bd-d',
};

const renderStars = (note: string | null) => {
  if (!note) return <span style={{ color: 'var(--tx-3)' }}>—</span>;
  const n = Number(note);
  if (Number.isNaN(n)) return <span style={{ color: 'var(--tx-3)' }}>—</span>;
  const full = Math.floor(n);
  const half = n - full >= 0.5;
  return (
    <span style={{ display: 'inline-flex', gap: 1, alignItems: 'center' }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={11}
          fill={i < full ? 'var(--warning)' : (i === full && half ? 'url(#half)' : 'transparent')}
          color={i < full || (i === full && half) ? 'var(--warning)' : 'var(--tx-3)'}
        />
      ))}
      <span style={{ fontSize: 10, marginLeft: 4, color: 'var(--tx-3)' }}>{n.toFixed(1)}</span>
    </span>
  );
};

export const SousTraitantsPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [statutFilter, setStatutFilter] = useState<StatutSousTraitant | ''>('');
  const [specFilter, setSpecFilter] = useState<SpecialiteSousTraitant | ''>('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const queryParams = useMemo(() => ({
    limit: 500,
    statut:     statutFilter || undefined,
    specialite: specFilter || undefined,
    search:     search.trim() || undefined,
  }), [statutFilter, specFilter, search]);

  const { data, isLoading, refetch } = useSousTraitants(queryParams);
  const sts = data?.data ?? [];
  const update = useUpdateSousTraitant();

  // KPIs
  const totalActifs    = sts.filter((s) => s.statut === 'actif').length;
  const delaiMoyen     = sts.filter((s) => s.delaiMoyenJours).length > 0
    ? Math.round(sts.filter((s) => s.delaiMoyenJours).reduce((acc, s) => acc + (s.delaiMoyenJours ?? 0), 0) /
        sts.filter((s) => s.delaiMoyenJours).length)
    : 0;
  const stsAvecNote = sts.filter((s) => s.qualiteNote);
  const qualiteMoyenne = stsAvecNote.length > 0
    ? (stsAvecNote.reduce((acc, s) => acc + Number(s.qualiteNote ?? '0'), 0) / stsAvecNote.length).toFixed(1)
    : '—';

  const toggleStatut = async (s: SousTraitant) => {
    setError(null);
    const newStatut: StatutSousTraitant = s.statut === 'actif' ? 'inactif' : 'actif';
    try { await update.mutateAsync({ id: s.id, payload: { statut: newStatut } }); }
    catch (err) { setError(extractError(err).message); }
  };

  return (
    <>
      <Topbar
        title="Sous-traitants Impression"
        subtitle={filiale ? `${sts.length} sous-traitant(s) · ${filiale.fullName}` : 'Sous-traitants'}
        actions={
          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          </button>
        }
      />
      <div className="ds-content">
        {error && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{error}</div>
          </div>
        )}

        <div className="ds-kpi-row" style={{ marginBottom: 16 }}>
          <div className="ds-kr kr-c1">
            <div className="ds-kr-ico"><Handshake size={18} /></div>
            <div className="ds-kr-val">{sts.length}</div>
            <div className="ds-kr-lbl">Sous-traitants</div>
          </div>
          <div className="ds-kr kr-c2">
            <div className="ds-kr-ico">✅</div>
            <div className="ds-kr-val">{totalActifs}</div>
            <div className="ds-kr-lbl">Actifs</div>
          </div>
          <div className="ds-kr kr-c3">
            <div className="ds-kr-ico"><Clock size={18} /></div>
            <div className="ds-kr-val">{delaiMoyen || '—'}</div>
            <div className="ds-kr-lbl">Délai moyen (jours)</div>
          </div>
          <div className="ds-kr kr-c4">
            <div className="ds-kr-ico"><Star size={18} /></div>
            <div className="ds-kr-val">{qualiteMoyenne}</div>
            <div className="ds-kr-lbl">Qualité moyenne /5</div>
          </div>
        </div>

        <div className="ds-filters" style={{ marginBottom: 12 }}>
          <input
            type="text"
            placeholder="Rechercher raison sociale / contact / email…"
            className="ds-fi"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 280 }}
          />
          <select className="ds-fi" value={statutFilter} onChange={(e) => setStatutFilter(e.target.value as StatutSousTraitant | '')} style={{ width: 'auto' }}>
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_SOUS_TRAITANT_LABELS) as StatutSousTraitant[]).map((s) => (
              <option key={s} value={s}>{STATUT_SOUS_TRAITANT_LABELS[s]}</option>
            ))}
          </select>
          <select className="ds-fi" value={specFilter} onChange={(e) => setSpecFilter(e.target.value as SpecialiteSousTraitant | '')} style={{ width: 'auto' }}>
            <option value="">Toutes spécialités</option>
            {(Object.keys(SPECIALITE_SOUS_TRAITANT_LABELS) as SpecialiteSousTraitant[]).map((s) => (
              <option key={s} value={s}>{SPECIALITE_SOUS_TRAITANT_LABELS[s]}</option>
            ))}
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Sous-traitants</div>
              <div className="ds-table-sub">
                {sts.length} sous-traitant{sts.length > 1 ? 's' : ''} — MVP Sprint 2 (CRUD complet bientôt)
              </div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th>Raison sociale</th>
                <th style={{ width: 140 }}>Spécialité</th>
                <th style={{ width: 130 }}>Contact</th>
                <th style={{ width: 120 }}>Téléphone</th>
                <th style={{ width: 90, textAlign: 'right' }}>Délai (j)</th>
                <th style={{ width: 110 }}>Qualité</th>
                <th style={{ width: 130 }}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>Chargement…</td></tr>
              ) : sts.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>Aucun sous-traitant enregistré. Créez-en un via API ou la prochaine UI (Sprint 2.5).</td></tr>
              ) : sts.map((s) => (
                <tr key={s.id}>
                  <td style={{ fontWeight: 500 }}>{s.raisonSociale}</td>
                  <td style={{ fontSize: 12 }}>
                    {s.specialite ? SPECIALITE_SOUS_TRAITANT_LABELS[s.specialite] : <span style={{ color: 'var(--tx-3)' }}>—</span>}
                    {s.specialite === 'autre' && s.specialiteAutre && <span style={{ color: 'var(--tx-3)' }}> ({s.specialiteAutre})</span>}
                  </td>
                  <td style={{ fontSize: 12 }}>{s.contact ?? '—'}</td>
                  <td style={{ fontSize: 12 }}>{s.phone ?? '—'}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{s.delaiMoyenJours ?? '—'}</td>
                  <td>{renderStars(s.qualiteNote)}</td>
                  <td>
                    <button
                      className={`ds-btn ds-btn-sm ${STATUT_BADGE[s.statut].replace('ds-bd', 'ds-btn')}`}
                      onClick={() => void toggleStatut(s)}
                      disabled={update.isPending || s.statut === 'blackliste'}
                      title={s.statut === 'blackliste' ? 'Blacklisté — modifiable seulement par admin' : 'Cliquer pour toggle actif/inactif'}
                    >
                      {STATUT_SOUS_TRAITANT_LABELS[s.statut]}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
